// Serviço do módulo de Atendimento (inbox de WhatsApp).
// Envio passa por /api/v1/whatsapp/enviar — o token nunca vem ao browser.
import { API_ROUTES, apiFetch } from '@/config/api-routes'
import { createClient } from '@/lib/supabase/client'
import { registrarAuditoria } from '@/services/identity'
import type { CategoriaMensagem, Mensagem, StatusThread } from '@/services/mensagens'

export interface Conversa {
  id: string
  workspace_id: string
  paciente_id: string | null
  paciente_nome: string | null
  wa_telefone: string | null
  contato_nome: string | null
  categoria: CategoriaMensagem
  prioridade: 'baixa' | 'normal' | 'alta' | 'urgente'
  status: StatusThread
  janela_expira_em: string | null
  nao_lidas: number
  ultima_mensagem_em: string | null
  atendente_user_id: string | null
  canal_conexao_id: string | null
  created_at: string
}

export interface CanalConexao {
  id: string
  provedor: 'simulado' | 'cloud_api'
  nome: string
  telefone_exibicao: string | null
  phone_number_id: string | null
  verify_token: string | null
  status: 'desconectado' | 'conectado' | 'erro'
  ativo: boolean
}

export interface WhatsTemplate {
  id: string
  nome: string
  idioma: string
  categoria: string
  status: string
  corpo: string
  variaveis: number
}

function mapConversa(row: Record<string, unknown>): Conversa {
  const raw = row.pacientes
  const pac = (Array.isArray(raw) ? raw[0] : raw) as { nome: string } | null
  const { pacientes: _drop, ...resto } = row
  void _drop
  return { ...(resto as unknown as Omit<Conversa, 'paciente_nome'>), paciente_nome: pac?.nome ?? null }
}

/** Nome de exibição: paciente cadastrada > nome do perfil WhatsApp > telefone. */
export function nomeConversa(c: Conversa): string {
  if (c.paciente_nome) return c.paciente_nome
  if (c.contato_nome) return c.contato_nome
  if (c.wa_telefone) return formatarTelefone(c.wa_telefone)
  return 'Contato'
}

export function formatarTelefone(t: string): string {
  const d = t.replace(/\D/g, '')
  const nac = d.startsWith('55') ? d.slice(2) : d
  if (nac.length === 11) return `(${nac.slice(0, 2)}) ${nac.slice(2, 7)}-${nac.slice(7)}`
  if (nac.length === 10) return `(${nac.slice(0, 2)}) ${nac.slice(2, 6)}-${nac.slice(6)}`
  return `+${d}`
}

export function janelaAberta(c: Pick<Conversa, 'janela_expira_em'>): boolean {
  return c.janela_expira_em ? new Date(c.janela_expira_em) > new Date() : false
}

export function horasRestantes(c: Pick<Conversa, 'janela_expira_em'>): number {
  if (!c.janela_expira_em) return 0
  const ms = new Date(c.janela_expira_em).getTime() - Date.now()
  return Math.max(0, Math.floor(ms / 3600000))
}

export async function listConversas(workspaceId: string, filtro: {
  aba?: 'todas' | 'nao_lidas' | 'minhas' | 'sem_atendente' | 'encerradas'
  userId?: string
  busca?: string
} = {}): Promise<Conversa[]> {
  const supabase = createClient()
  let q = supabase
    .from('mensagem_threads')
    .select('id, workspace_id, paciente_id, wa_telefone, contato_nome, categoria, prioridade, status, janela_expira_em, nao_lidas, ultima_mensagem_em, atendente_user_id, canal_conexao_id, created_at, pacientes(nome)')
    .eq('workspace_id', workspaceId)
    .not('wa_telefone', 'is', null)
    .order('ultima_mensagem_em', { ascending: false, nullsFirst: false })
    .limit(200)

  if (filtro.aba === 'encerradas') q = q.eq('status', 'encerrada')
  else q = q.neq('status', 'encerrada')
  if (filtro.aba === 'nao_lidas') q = q.gt('nao_lidas', 0)
  if (filtro.aba === 'minhas' && filtro.userId) q = q.eq('atendente_user_id', filtro.userId)
  if (filtro.aba === 'sem_atendente') q = q.is('atendente_user_id', null)

  const { data, error } = await q
  if (error) throw error
  let rows = (data ?? []).map(r => mapConversa(r as Record<string, unknown>))
  if (filtro.busca?.trim()) {
    const b = filtro.busca.trim().toLowerCase()
    rows = rows.filter(c => nomeConversa(c).toLowerCase().includes(b) || (c.wa_telefone ?? '').includes(b.replace(/\D/g, '')))
  }
  return rows
}

export async function getConversa(workspaceId: string, id: string): Promise<{ conversa: Conversa; mensagens: Mensagem[] } | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('mensagem_threads')
    .select('id, workspace_id, paciente_id, wa_telefone, contato_nome, categoria, prioridade, status, janela_expira_em, nao_lidas, ultima_mensagem_em, atendente_user_id, canal_conexao_id, created_at, pacientes(nome)')
    .eq('workspace_id', workspaceId)
    .eq('id', id)
    .maybeSingle()
  if (error) throw error
  if (!data) return null
  const { data: msgs } = await supabase
    .from('mensagens').select('*').eq('thread_id', id).order('created_at')
  return { conversa: mapConversa(data as Record<string, unknown>), mensagens: (msgs ?? []) as Mensagem[] }
}

/** Claim atômico: se outra pessoa já assumiu, devolve ok:false com quem está atendendo. */
export async function atenderConversa(threadId: string): Promise<{ ok: boolean; atendente_user_id: string | null }> {
  const supabase = createClient()
  const { data, error } = await supabase.rpc('atender_conversa', { p_thread: threadId })
  if (error) throw error
  return data as { ok: boolean; atendente_user_id: string | null }
}

export async function liberarConversa(threadId: string): Promise<void> {
  const supabase = createClient()
  await supabase.rpc('liberar_conversa', { p_thread: threadId })
}

export async function marcarLida(threadId: string): Promise<void> {
  const supabase = createClient()
  await supabase.rpc('marcar_conversa_lida', { p_thread: threadId })
}

export async function enviarWhatsApp(params: {
  threadId: string
  texto?: string
  template?: { nome: string; idioma: string; variaveis: string[] }
}): Promise<{ ok: boolean; erro?: string; janelaFechada?: boolean }> {
  const r = await apiFetch(API_ROUTES.whatsapp.enviar, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) return { ok: false, erro: j.erro ?? `falha (${r.status})`, janelaFechada: j.janelaFechada }
  return { ok: true }
}

export async function simularRecebida(params: { telefone: string; texto: string; nome?: string }): Promise<{ ok: boolean; erro?: string; threadId?: string }> {
  const r = await apiFetch(API_ROUTES.whatsapp.simularRecebida, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(params),
  })
  const j = await r.json().catch(() => ({}))
  if (!r.ok) return { ok: false, erro: j.erro ?? `falha (${r.status})` }
  return { ok: true, threadId: j.threadId }
}

export async function vincularPaciente(workspaceId: string, threadId: string, pacienteId: string): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('mensagem_threads')
    .update({ paciente_id: pacienteId })
    .eq('id', threadId)
    .eq('workspace_id', workspaceId)
  if (error) throw error
  await registrarAuditoria({
    workspaceId, acao: 'conversa_vinculada_paciente', entidade: 'mensagem_threads',
    registroId: threadId, dados: { paciente_id: pacienteId },
  })
}

// ---------- conexão e templates ----------

export async function getCanal(workspaceId: string): Promise<CanalConexao | null> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('canais_conexao')
    .select('id, provedor, nome, telefone_exibicao, phone_number_id, verify_token, status, ativo')
    .eq('workspace_id', workspaceId)
    .order('created_at')
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as CanalConexao | null) ?? null
}

export async function salvarCanal(params: {
  workspaceId: string
  id?: string
  provedor: 'simulado' | 'cloud_api'
  nome: string
  telefoneExibicao?: string | null
  phoneNumberId?: string | null
  verifyToken?: string | null
}): Promise<string> {
  const supabase = createClient()
  const payload = {
    workspace_id: params.workspaceId,
    provedor: params.provedor,
    nome: params.nome.trim(),
    telefone_exibicao: params.telefoneExibicao ?? null,
    phone_number_id: params.phoneNumberId || null,
    verify_token: params.verifyToken || null,
    status: params.provedor === 'simulado' ? 'conectado' : 'desconectado',
  }
  if (params.id) {
    const { error } = await supabase.from('canais_conexao').update(payload).eq('id', params.id)
    if (error) throw error
    return params.id
  }
  const { data, error } = await supabase.from('canais_conexao').insert(payload).select('id').single()
  if (error) throw error
  return (data as { id: string }).id
}

export interface MensageriaConfig {
  whatsapp_ativo: boolean
  envio_automatico: boolean
  teto_envios_hora: number
}

export async function getMensageriaConfig(workspaceId: string): Promise<MensageriaConfig> {
  const supabase = createClient()
  const { data } = await supabase
    .from('mensageria_config')
    .select('whatsapp_ativo, envio_automatico, teto_envios_hora')
    .eq('workspace_id', workspaceId)
    .maybeSingle()
  return (data as MensageriaConfig | null) ?? { whatsapp_ativo: true, envio_automatico: false, teto_envios_hora: 60 }
}

export async function salvarMensageriaConfig(workspaceId: string, cfg: MensageriaConfig): Promise<void> {
  const supabase = createClient()
  const { error } = await supabase
    .from('mensageria_config')
    .upsert({ workspace_id: workspaceId, ...cfg, atualizado_em: new Date().toISOString() })
  if (error) throw error
  await registrarAuditoria({
    workspaceId, acao: 'mensageria_config_alterada', entidade: 'mensageria_config', dados: cfg as unknown as Record<string, unknown>,
  })
}

export async function listTemplates(workspaceId: string): Promise<WhatsTemplate[]> {
  const supabase = createClient()
  const { data, error } = await supabase
    .from('whatsapp_templates')
    .select('id, nome, idioma, categoria, status, corpo, variaveis')
    .eq('workspace_id', workspaceId)
    .order('nome')
  if (error) throw error
  return (data ?? []) as WhatsTemplate[]
}

export async function criarTemplate(params: {
  workspaceId: string
  canalId: string | null
  nome: string
  corpo: string
  categoria?: string
}): Promise<void> {
  const supabase = createClient()
  const variaveis = (params.corpo.match(/\{\{\d+\}\}/g) ?? []).length
  const { error } = await supabase.from('whatsapp_templates').insert({
    workspace_id: params.workspaceId,
    canal_conexao_id: params.canalId,
    nome: params.nome.trim().toLowerCase().replace(/[^a-z0-9_]/g, '_'),
    corpo: params.corpo.trim(),
    categoria: params.categoria ?? 'UTILITY',
    variaveis,
    status: 'RASCUNHO',
  })
  if (error) throw error
}

export async function aprovarTemplateLocal(workspaceId: string, id: string): Promise<void> {
  // No provedor simulado marcamos como aprovado localmente.
  // Com Cloud API, o status vem da sincronização com a Meta.
  const supabase = createClient()
  const { error } = await supabase
    .from('whatsapp_templates').update({ status: 'APPROVED' })
    .eq('id', id).eq('workspace_id', workspaceId)
  if (error) throw error
}

/** Preenche {{1}}, {{2}}… do corpo do template. */
export function preencherTemplate(corpo: string, variaveis: string[]): string {
  return corpo.replace(/\{\{(\d+)\}\}/g, (_m, i) => variaveis[Number(i) - 1] ?? `{{${i}}}`)
}
